import React, { useMemo, useRef, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { EditorView } from '@codemirror/view';
import { html } from '@codemirror/lang-html';
import { HiOutlineCode, HiOutlineExclamation, HiOutlineTemplate } from 'react-icons/hi';
import { EmailBlock, Template } from '../../../../../../../types/communication';
import { blocksToHtml, htmlBodyContent, looksLikeLayoutHtml } from '../../../../../../../utils/emailHtml';
import { variableToken } from '../../../../../../../utils/templateVariables';
import SectionCard from '../../../shared/SectionCard';
import ConstantPicker from '../../../shared/ConstantPicker';
import VariablePicker from '../../../shared/VariablePicker';
import EmailBlockList from './EmailBlockList';

interface EmailContentEditorProps {
    template: Template;
    disabled: boolean;
    onTemplateChange: (patch: Partial<Template>) => void;
}

type View = 'VISUAL' | 'CODE';

const SNIPPETS: { label: string; text: string }[] = [
    {
        label: 'Tabela de itens (laço)',
        text: '{%- for row in LISTA %}\n<tr>\n    <td style="padding:5px;border:1px solid #ddd;">{{ row.CAMPO }}</td>\n</tr>\n{%- endfor %}',
    },
    { label: 'Condição', text: '{% if VARIAVEL %}\n<p>texto quando houver</p>\n{% endif %}' },
    { label: 'Valor padrão', text: "{{ VARIAVEL or '—' }}" },
];

const ViewSwitch: React.FC<{ view: View; onChange: (view: View) => void }> = ({ view, onChange }) => (
    <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-700 p-0.5 text-xs">
        {[
            { key: 'VISUAL' as const, label: 'Visual', icon: <HiOutlineTemplate size={14} /> },
            { key: 'CODE' as const, label: 'Código HTML (avançado)', icon: <HiOutlineCode size={14} /> },
        ].map((option) => (
            <button
                key={option.key}
                type="button"
                onClick={() => onChange(option.key)}
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 ${
                    view === option.key ? 'bg-white dark:bg-slate-900 shadow text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-300'
                }`}
            >
                {option.icon}
                {option.label}
            </button>
        ))}
    </div>
);

const EmailContentEditor: React.FC<EmailContentEditorProps> = ({ template, disabled, onTemplateChange }) => {
    const viewRef = useRef<EditorView | null>(null);
    const [view, setView] = useState<View>('VISUAL');
    const fromBlocks = template.editorMode === 'BLOCKS' && Boolean(template.blocks);
    const layout = !fromBlocks && looksLikeLayoutHtml(template.content);

    const visualBlocks: EmailBlock[] = useMemo(
        () => (fromBlocks ? template.blocks ?? [] : [{ id: `html-${template.id}`, kind: 'TEXT', text: htmlBodyContent(template.content) }]),
        [fromBlocks, template.blocks, template.content, template.id],
    );

    const insertAtCursor = (text: string) => {
        const editor = viewRef.current;
        if (!editor) {
            onTemplateChange({ editorMode: 'CODE', content: `${template.content}${text}` });
            return;
        }
        const { from, to } = editor.state.selection.main;
        editor.dispatch({ changes: { from, to, insert: text }, selection: { anchor: from + text.length } });
        editor.focus();
    };

    const codeValue = fromBlocks ? blocksToHtml(template.blocks ?? []) : template.content;

    return (
        <SectionCard
            title="Corpo do e-mail"
            description={
                view === 'VISUAL'
                    ? 'Escreva o e-mail como num editor de texto. A pré-visualização ao lado mostra como ele chega.'
                    : 'HTML do e-mail, como fica gravado no SES. Para quem conhece HTML.'
            }
            actions={<ViewSwitch view={view} onChange={setView} />}
        >
            {view === 'VISUAL' ? (
                <>
                    {layout && !disabled && (
                        <div className="flex items-start gap-2 mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                            <HiOutlineExclamation size={16} className="shrink-0 mt-0.5" />
                            <span>
                                Este e-mail tem layout próprio (moldura, tabelas ou repetição de itens). O visual mostra só o texto: se você editar
                                aqui, esse layout vira texto simples. Para mexer mantendo o layout, use o Código HTML.
                            </span>
                        </div>
                    )}
                    <EmailBlockList
                        key={template.id}
                        blocks={visualBlocks}
                        variables={template.variables}
                        disabled={disabled}
                        onChange={(blocks) => onTemplateChange({ editorMode: 'BLOCKS', blocks })}
                    />
                </>
            ) : (
                <>
                    {!disabled && (
                        <div className="flex flex-wrap items-center gap-1 mb-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 px-1.5 py-1">
                            {SNIPPETS.map((snippet) => (
                                <button
                                    key={snippet.label}
                                    type="button"
                                    onClick={() => insertAtCursor(snippet.text)}
                                    className="h-7 px-2 rounded-md text-xs text-gray-600 dark:text-gray-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    + {snippet.label}
                                </button>
                            ))}
                            <span className="flex-1" />
                            <VariablePicker variables={template.variables} onPick={(name) => insertAtCursor(variableToken(name))} />
                            <ConstantPicker onPick={(name) => insertAtCursor(variableToken(name))} />
                        </div>
                    )}
                    <div className="rounded-lg border border-gray-300 dark:border-slate-600 overflow-hidden">
                        <CodeMirror
                            value={codeValue}
                            editable={!disabled}
                            onChange={(content) => onTemplateChange({ editorMode: 'CODE', content })}
                            onCreateEditor={(editor) => {
                                viewRef.current = editor;
                            }}
                            height="560px"
                            theme="light"
                            extensions={[EditorView.lineWrapping, html()]}
                        />
                    </div>
                </>
            )}
        </SectionCard>
    );
};

export default EmailContentEditor;
